import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../i18n";
import { localizePath } from "../i18n/routes";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const OG_IMAGE = "/og-image.jpg";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

export function setJsonLd(id, data) {
  const key = `jsonld-${id}`;
  let el = document.getElementById(key);
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = key;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : null;
  useEffect(() => {
    setJsonLd(id, json ? JSON.parse(json) : null);
    return () => setJsonLd(id, null);
  }, [id, json]);
}

export function useSEO(title, description, { image, type = "website" } = {}) {
  const { lang } = useLang();
  const { pathname } = useLocation();
  useEffect(() => {
    const here = ORIGIN + pathname;
    const en = ORIGIN + localizePath(pathname, "en");
    const az = ORIGIN + localizePath(pathname, "az");
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", here);
    upsertMeta("property", "og:image", ORIGIN + (image || OG_IMAGE));
    upsertMeta("property", "og:site_name", "YUXEL");
    upsertMeta("property", "og:locale", lang === "az" ? "az_AZ" : "en_US");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ORIGIN + (image || OG_IMAGE));
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: here });
    upsertLink('link[hreflang="en"]', { rel: "alternate", hreflang: "en", href: en });
    upsertLink('link[hreflang="az"]', { rel: "alternate", hreflang: "az", href: az });
    upsertLink('link[hreflang="x-default"]', { rel: "alternate", hreflang: "x-default", href: en });
  }, [title, description, image, type, lang, pathname]);
}

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: ORIGIN + it.path,
  })),
});

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "YUXEL",
  alternateName: "YUXEL Business Transformation Company",
  url: ORIGIN,
  logo: ORIGIN + "/favicon.svg",
  image: ORIGIN + OG_IMAGE,
  description:
    "Business transformation company building strategy, brand, marketing and sales systems for medium and large businesses.",
  founder: { "@type": "Person", name: "Zülfinaz Dəmirova", jobTitle: "Founder" },
  address: { "@type": "PostalAddress", addressLocality: "Baku", addressCountry: "AZ" },
  areaServed: ["AZ"],
  knowsLanguage: ["az", "en"],
  contactPoint: { "@type": "ContactPoint", contactType: "sales", url: ORIGIN + "/contact", availableLanguage: ["Azerbaijani", "English"] },
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "YUXEL",
  url: ORIGIN,
  inLanguage: ["az", "en"],
  publisher: { "@type": "Organization", name: "YUXEL" },
};
